import { useMemo, useCallback, useEffect } from 'react';
import { Task } from '../../types';
import { formatDate, parseDate, todayStr } from '../../utils/dateUtils';

interface UseGanttTimelineGeometryParams {
  isGanttActive: boolean;
  visibleTasks: Task[];
  tasks: Task[];
  datePadding: number;
  hideEmptyDays: boolean;
  dayWidth: number;
  timelineDatesRef: React.MutableRefObject<Date[]>;
  dateToIndexMapRef: React.MutableRefObject<Record<string, number>>;
}

export function useGanttTimelineGeometry({
  isGanttActive,
  visibleTasks,
  tasks,
  datePadding,
  hideEmptyDays,
  dayWidth,
  timelineDatesRef,
  dateToIndexMapRef,
}: UseGanttTimelineGeometryParams) {
  const leftPadding = datePadding;
  const rightPadding = datePadding;

  const { gridStart, gridEnd } = useMemo(() => {
    if (!isGanttActive) {
      const d = parseDate(todayStr);
      return { gridStart: d, gridEnd: d };
    }
    const datedTasks = visibleTasks.filter(t => t.start || t.end);
    if (datedTasks.length === 0) {
      const d = parseDate(todayStr);
      const s = new Date(d); s.setDate(s.getDate() - leftPadding);
      const e = new Date(d); e.setDate(e.getDate() + rightPadding);
      return { gridStart: s, gridEnd: e };
    }
    const timestamps = datedTasks
      .flatMap(t => [parseDate(t.start || t.end).getTime(), parseDate(t.end || t.start).getTime()])
      .filter(ts => !Number.isNaN(ts));
    const s = new Date(timestamps.length > 0 ? Math.min(...timestamps) : Date.now());
    const e = new Date(timestamps.length > 0 ? Math.max(...timestamps) : Date.now());

    // Strictly normalize to midnight
    s.setHours(0, 0, 0, 0);
    e.setHours(0, 0, 0, 0);

    s.setDate(s.getDate() - leftPadding);
    e.setDate(e.getDate() + rightPadding);

    // Re-normalize after shift (for DST)
    s.setHours(0, 0, 0, 0);
    e.setHours(0, 0, 0, 0);

    return { gridStart: s, gridEnd: e };
  }, [isGanttActive, visibleTasks, leftPadding, rightPadding]);

  const timelineDates = useMemo(() => {
    if (!isGanttActive) return [];
    const res: Date[] = [];
    const curr = new Date(gridStart);
    const maxDays = 3650;
    let count = 0;
    const activeDays = new Set<string>();
    const datedTasks = visibleTasks.filter(t => t.start || t.end);

    if (hideEmptyDays) {
      datedTasks.forEach(t => {
        const d = parseDate(t.start || t.end);
        const targetEnd = parseDate(t.end || t.start);
        let loopLimit = 0;
        while (d <= targetEnd && loopLimit < maxDays) {
          activeDays.add(formatDate(d));
          d.setDate(d.getDate() + 1);
          loopLimit++;
        }
      });

      // Ensure padding days are also active so they aren't hidden
      if (datedTasks.length > 0) {
        const timestamps = datedTasks
          .flatMap(t => [parseDate(t.start || t.end).getTime(), parseDate(t.end || t.start).getTime()])
          .filter(ts => !Number.isNaN(ts));
        const firstTaskStart = new Date(timestamps.length > 0 ? Math.min(...timestamps) : Date.now());
        const lastTaskEnd = new Date(timestamps.length > 0 ? Math.max(...timestamps) : Date.now());

        // Add padding before
        let p = new Date(gridStart);
        let beforeLimit = 0;
        while (p < firstTaskStart && beforeLimit < maxDays) {
          activeDays.add(formatDate(p));
          p.setDate(p.getDate() + 1);
          beforeLimit++;
        }

        // Add padding after
        p = new Date(lastTaskEnd);
        p.setDate(p.getDate() + 1); // Start after the task end
        let afterLimit = 0;
        while (p <= gridEnd && afterLimit < maxDays) {
          activeDays.add(formatDate(p));
          p.setDate(p.getDate() + 1);
          afterLimit++;
        }
      }
    }
    while (curr <= gridEnd && count < maxDays) {
      if (!hideEmptyDays || activeDays.has(formatDate(curr))) {
        res.push(new Date(curr));
      }
      curr.setDate(curr.getDate() + 1);
      count++;
    }
    return res;
  }, [isGanttActive, gridStart, gridEnd, hideEmptyDays, visibleTasks]);

  const dateToIndexMap = useMemo(() => {
    if (!isGanttActive) return {};
    const map: Record<string, number> = {};
    timelineDates.forEach((d, i) => { map[formatDate(d)] = i; });
    return map;
  }, [isGanttActive, timelineDates]);

  useEffect(() => { timelineDatesRef.current = timelineDates; }, [timelineDates, timelineDatesRef]);
  useEffect(() => { dateToIndexMapRef.current = dateToIndexMap; }, [dateToIndexMap, dateToIndexMapRef]);

  const headerDates = useMemo(() => {
    if (!isGanttActive) return [];
    const shortWeekdayFormatter = new Intl.DateTimeFormat('de-DE', { weekday: 'short' });
    return timelineDates.map((d, i) => {
      const nextDateInTimeline = timelineDates[i + 1];
      let hasGapAfter = false;
      if (nextDateInTimeline) {
        const logicallyNext = new Date(d);
        logicallyNext.setDate(logicallyNext.getDate() + 1);
        if (formatDate(logicallyNext) !== formatDate(nextDateInTimeline)) {
          hasGapAfter = true;
        }
      }
      return {
        date: d.getTime(),
        dayNum: d.getDate(),
        weekday: shortWeekdayFormatter.format(d).toUpperCase(),
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
        isFirst: d.getDate() === 1,
        hasGapAfter
      };
    });
  }, [isGanttActive, timelineDates]);

  const months = useMemo(() => {
    if (!isGanttActive) return [];
    const res: { name: string; days: number }[] = [];
    const longMonthYearFormatter = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });
    timelineDates.forEach(date => {
      const monthYear = longMonthYearFormatter.format(date);
      if (res.length === 0 || res[res.length - 1].name !== monthYear) res.push({ name: monthYear, days: 1 });
      else res[res.length - 1].days++;
    });
    return res;
  }, [isGanttActive, timelineDates]);

  const taskIdMap = useMemo(() => {
    if (!isGanttActive) return new Map<string, number>();
    const map = new Map<string, number>();
    visibleTasks.forEach((t, i) => map.set(String(t.id), i));
    return map;
  }, [isGanttActive, visibleTasks]);

  const fullTaskMap = useMemo(() => {
    if (!isGanttActive) return new Map<string, Task>();
    const map = new Map<string, Task>();
    tasks.forEach(t => map.set(String(t.id), t));
    return map;
  }, [isGanttActive, tasks]);

  const getTaskX = useCallback((date: string) => (dateToIndexMap[date] ?? 0) * dayWidth, [dateToIndexMap, dayWidth]);

  const getTaskWidth = useCallback((start: string, end: string) => {
    const s = dateToIndexMap[start];
    const e = dateToIndexMap[end];
    if (s === undefined || e === undefined) return dayWidth;
    return Math.max(dayWidth, (e - s + 1) * dayWidth);
  }, [dateToIndexMap, dayWidth]);

  const getDependencyPath = useCallback((fromId: string | number, toId: string | number) => {
    const fromIndex = taskIdMap.get(String(fromId));
    const toIndex = taskIdMap.get(String(toId));
    if (fromIndex === undefined || toIndex === undefined) return null;

    const fromTask = visibleTasks[fromIndex];
    const toTask = visibleTasks[toIndex];
    if (!fromTask || !toTask) return null;

    if (!fromTask.start || !fromTask.end || !toTask.start || !toTask.end) return null;

    const rowH = 48;
    const startX = getTaskX(fromTask.start) + getTaskWidth(fromTask.start, fromTask.end);
    const startY = fromIndex * rowH + 24;
    const endX = getTaskX(toTask.start);
    const endY = toIndex * rowH + 24;

    const margin = 12;
    const midX = startX + margin;

    if (endX >= midX) {
      return `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${endY} L ${endX} ${endY}`;
    } else {
      const backwardMidY = startY + (endY - startY) / 2;
      const safeEndX = endX - margin;
      return `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${backwardMidY} L ${safeEndX} ${backwardMidY} L ${safeEndX} ${endY} L ${endX} ${endY}`;
    }
  }, [taskIdMap, visibleTasks, getTaskX, getTaskWidth]);

  return {
    leftPadding,
    rightPadding,
    gridStart,
    gridEnd,
    timelineDates,
    dateToIndexMap,
    headerDates,
    months,
    taskIdMap,
    fullTaskMap,
    getTaskX,
    getTaskWidth,
    getDependencyPath,
  };
}
